import React from "react";
import { useTranslation } from "react-i18next";
import {
  commitRemindersOptout,
  resolveRemindersOptout,
} from "@/lib/services/remindersPublicService";

/**
 * ?action=reminders_optout&token=… landing (return-reminders README §4.5
 * in the backend repo). Confirm-then-POST: the GET resolve is pure, the
 * opt-out commits only on the button press — a prefetched link must never
 * unsubscribe anyone. Shown INSTEAD of the booking flow.
 */
export default function RemindersOptoutView({ token }: { token: string }) {
  const { t } = useTranslation();
  const [state, setState] = React.useState<
    "loading" | "confirm" | "done" | "invalid" | "error"
  >("loading");
  const [salon, setSalon] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await resolveRemindersOptout(token);
        if (!alive) return;
        setSalon(res.company_title || "");
        setState(res.already_opted_out ? "done" : "confirm");
      } catch (e) {
        if (!alive) return;
        const status = (e as any)?.response?.status;
        setState(status === 404 ? "invalid" : "error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const confirm = async () => {
    setBusy(true);
    try {
      await commitRemindersOptout(token);
      setState("done");
    } catch (e) {
      const status = (e as any)?.response?.status;
      setState(status === 404 ? "invalid" : "error");
    } finally {
      setBusy(false);
    }
  };

  const shell = (children: React.ReactNode) => (
    <div
      className="font-sans"
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "50vh", padding: "2rem",
        textAlign: "center", color: "#374151",
      }}
    >
      {children}
    </div>
  );

  if (state === "loading") {
    return shell(<div>{t("widget.reminders_optout.loading", "One moment…")}</div>);
  }

  if (state === "invalid") {
    return shell(
      <>
        <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🔗</div>
        <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: ".5rem" }}>
          {t("widget.reminders_optout.invalid_title", "This link is no longer valid")}
        </h2>
        <p style={{ maxWidth: 420, color: "#6b7280" }}>
          {t("widget.reminders_optout.invalid_body",
            "The unsubscribe link could not be found. If you keep receiving reminders, use the link from the most recent email.")}
        </p>
      </>,
    );
  }

  if (state === "error") {
    return shell(
      <p style={{ maxWidth: 420, color: "#6b7280" }}>
        {t("widget.reminders_optout.error",
          "Something went wrong — please try the link again in a minute.")}
      </p>,
    );
  }

  if (state === "done") {
    return shell(
      <>
        <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>✅</div>
        <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: ".5rem" }}>
          {t("widget.reminders_optout.done_title", "Reminders stopped")}
        </h2>
        <p style={{ maxWidth: 420, color: "#6b7280" }}>
          {salon
            ? t("widget.reminders_optout.done_body",
                "{{salon}} won't send you any more return reminders. Booking confirmations are not affected.",
                { salon })
            : t("widget.reminders_optout.done_body_generic",
                "You won't receive any more return reminders from this salon. Booking confirmations are not affected.")}
        </p>
      </>,
    );
  }

  return shell(
    <>
      <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>🔕</div>
      <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: ".5rem" }}>
        {salon
          ? t("widget.reminders_optout.confirm_title",
              "Stop reminders from {{salon}}?", { salon })
          : t("widget.reminders_optout.confirm_title_generic",
              "Stop reminders from this salon?")}
      </h2>
      <p style={{ maxWidth: 420, color: "#6b7280", marginBottom: "1.25rem" }}>
        {t("widget.reminders_optout.confirm_body",
          "You'll no longer get \"time for your next visit\" emails from this salon. Booking confirmations still arrive as usual.")}
      </p>
      <button
        type="button"
        onClick={confirm}
        disabled={busy}
        style={{
          background: "#e11d48", color: "#ffffff", border: "none",
          borderRadius: 8, padding: "0.75rem 1.75rem", fontWeight: 700,
          cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
        }}
      >
        {t("widget.reminders_optout.confirm_button", "Yes, stop these reminders")}
      </button>
    </>,
  );
}
